import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronLeft, Printer } from "lucide-react";
import { ReportDateFilter, useReportRange, prettyDate } from "../components/ReportDateFilter";
import { CustomerFullHistory } from "../components/CustomerFullHistory";
import { istNow } from "../lib/dates";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const EMPTY = { total: { clients: 0, business: 0 }, new: { clients: 0, business: 0, client_pct: 0, business_pct: 0 }, existing: { clients: 0, business: 0, client_pct: 0, business_pct: 0 }, rows: [] };

function Block({ title, clients, business, cpct, bpct, tone, testid }) {
  return (
    <Card className={`p-5 bg-card border-border ${tone}`} data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">{title}</p>
      <div className="grid grid-cols-2 gap-4">
        <div><p className="text-xs text-muted-foreground">Clients</p><p className="font-display text-3xl">{clients}</p>{cpct != null && <p className="text-xs text-muted-foreground">{cpct}% of clients</p>}</div>
        <div><p className="text-xs text-muted-foreground">Business</p><p className="font-display text-3xl text-primary">{rupee(business)}</p>{bpct != null && <p className="text-xs text-muted-foreground">{bpct}% of business</p>}</div>
      </div>
    </Card>
  );
}

export default function NewVsExistingReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { branchParam } = useBranch();
  const rr = useReportRange("month");
  const [staffId, setStaffId] = useState("");
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState(EMPTY);
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const custHistoryId = new URLSearchParams(location.search).get("cust_history");

  useEffect(() => { api.get("/staff", { params: { branch_id: branchParam } }).then((r) => setStaff(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {}); }, [branchParam]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/new-vs-existing", { params: { start: rr.start, end: rr.end, branch_id: branchParam, staff_id: staffId || undefined } })
      .then((r) => setData(r.data)).catch(() => setData(EMPTY)).finally(() => setLoading(false));
  }, [rr.start, rr.end, branchParam, staffId]);
  useEffect(() => { load(); }, [load]);

  const lastMonth = () => { const t = istNow(); rr.pick("custom"); rr.setCs(fmt(new Date(t.getFullYear(), t.getMonth() - 1, 1))); rr.setCe(fmt(new Date(t.getFullYear(), t.getMonth(), 0))); setTimeout(rr.generate, 0); };
  const rows = data.rows.filter((r) => typeFilter === "all" || r.type.toLowerCase() === typeFilter);

  const exportCsv = () => {
    const head = ["Customer", "Mobile", "Type", "First Visit", "Visits", "Business", "Branch", "First Visit Staff"];
    const body = rows.map((r) => [r.customer_name, r.mobile, r.type, r.first_visit_date, r.visits, r.business, r.branch, r.first_visit_staff]);
    const csv = [head, ...body, [], ["Total Clients", data.total.clients, "Total Business", data.total.business], ["New Clients", data.new.clients, "New Business", data.new.business], ["Existing Clients", data.existing.clients, "Existing Business", data.existing.business]]
      .map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `new_vs_existing_${rr.start}_${rr.end}.csv`; a.click();
  };

  return (
    <div data-testid="new-vs-existing-page">
      <CustomerFullHistory customerId={custHistoryId} onClose={() => custHistoryId && navigate(-1)} />
      <Button variant="ghost" className="mb-2 text-muted-foreground" onClick={() => navigate("/reports")} data-testid="nve-back"><ChevronLeft size={16} className="mr-1" /> Reports</Button>
      <PageHeader title="New vs Existing Client Business" subtitle="Who is bringing the business — first-time clients or returning ones (Actual Business: real money, wallet excluded)"
        actions={<div className="flex gap-2"><Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="nve-export-csv">CSV</Button><Button variant="outline" className="rounded-sm" onClick={() => window.print()} data-testid="nve-print"><Printer size={15} className="mr-1" /> Print</Button></div>} />

      <Card className="p-4 bg-card border-border mb-6 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <ReportDateFilter r={rr} prefix="nve" />
          <Button variant="outline" className="rounded-sm" onClick={lastMonth} data-testid="nve-last-month">Last Month</Button>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div><label className="text-xs text-muted-foreground block mb-1">Staff</label>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className="h-10 rounded-md bg-secondary border border-border px-3 text-sm w-56" data-testid="nve-staff-filter">
              <option value="">All Staff</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <p className="text-xs text-muted-foreground ml-auto">Branch filter uses the top branch selector · {prettyDate(rr.start)} — {prettyDate(rr.end)} · {data.bills || 0} bills</p>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Block title="Total" clients={data.total.clients} business={data.total.business} testid="nve-total" />
        <Block title="New Clients" clients={data.new.clients} business={data.new.business} cpct={data.new.client_pct} bpct={data.new.business_pct} tone="border-emerald-700/40" testid="nve-new" />
        <Block title="Existing Clients" clients={data.existing.clients} business={data.existing.business} cpct={data.existing.client_pct} bpct={data.existing.business_pct} tone="border-sky-700/40" testid="nve-existing" />
      </div>

      <div className="flex gap-2 mb-3" data-testid="nve-type-tabs">
        {[["all", "All"], ["new", "New"], ["existing", "Existing"]].map(([k, l]) => (
          <Button key={k} size="sm" variant="ghost" onClick={() => setTypeFilter(k)} data-testid={`nve-tab-${k}`} className={`rounded-sm ${typeFilter === k ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary"}`}>{l}</Button>
        ))}
      </div>
      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{["Customer", "Mobile", "Type", "First Visit", "Visits (period)", "Business", "Branch", "First Visit Staff"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody data-testid="nve-rows">
            {loading && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
            {!loading && !rows.length && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8" data-testid="nve-empty">No bills in this range.</TableCell></TableRow>}
            {!loading && rows.map((r, i) => (
              <TableRow key={r.customer_id || r.mobile || i} data-testid={`nve-row-${i}`}>
                <TableCell>{r.customer_id ? <button className="font-medium hover:underline underline-offset-4 text-left" onClick={() => navigate(`${location.pathname}?cust_history=${r.customer_id}`)} data-testid={`nve-cust-${i}`}>{r.customer_name}</button> : <span>{r.customer_name}</span>}</TableCell>
                <TableCell className="font-mono text-xs">{r.mobile || "—"}</TableCell>
                <TableCell>{r.is_new ? <Badge className="bg-emerald-600">New</Badge> : <Badge variant="secondary">Existing</Badge>}</TableCell>
                <TableCell>{r.first_visit_date}</TableCell>
                <TableCell>{r.visits}</TableCell>
                <TableCell className="font-semibold text-primary">{rupee(r.business)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.branch}</TableCell>
                <TableCell>{r.first_visit_staff}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
